import { type PaymentsDO, type Payment, STATUS_TRANSLATIONS } from "../profile.models"
import templateHTML from './Payments.component.html?raw'
import { i18n } from "../../internationalize/i18n.service"
import styles from './Payments.styles.scss?raw'
import { Formatter } from "../../core/helpers/formatter"
import downloadIcon from '../../assets/icons/download.icon.svg?raw'
import type { ProfileService } from "../profile.service"

export class PaymentsComponent extends HTMLElement {
    private paymentsData: PaymentsDO = { payments: [] };
    private authChangeListener: () => void;

    constructor(private profileService: ProfileService = window.tbwServices.profileService) {
        super();
        this.authChangeListener = this.refresh.bind(this);
    }

    connectedCallback() {
        this.attachShadow({ mode: 'open' })

        const templateContainer = document.createElement('div');
        templateContainer.innerHTML = templateHTML;
        const template = templateContainer.querySelector('template');

        if (template) {
            const style = document.createElement('style');
            style.textContent = styles;
            this.shadowRoot?.appendChild(style);
            this.shadowRoot?.appendChild(template.content.cloneNode(true));
            this.shadowRoot?.querySelectorAll<HTMLElement>('[i18n]').forEach((elem) => {
                const key = elem.getAttribute('i18n');
                if (key) {
                    elem.innerText = i18n.t(key);
                }
            });
        }

        // Initialize immediately when component is mounted
        this.init();

        // Add auth change listener
        document.addEventListener('authChange', this.authChangeListener);
    }

    disconnectedCallback() {
        // Clean up event listener when component is removed
        document.removeEventListener('authChange', this.authChangeListener);
    }

    async refresh() {
        try {
            await this.clear();
            await this.init();
        } catch (error) {
            console.error('Error refreshing payments:', error);
            // Show error state to user if needed
            this.showMissingPayments();
        }
    }

    async clear() {
        this.paymentsData = { payments: [] };
        this.style.display = 'none';

        // Clear the table content
        const tbody = this.shadowRoot?.querySelector('tbody');
        if (tbody) {
            tbody.innerHTML = '';
        }
    }

    async init() {
        try {
            this.paymentsData = await this.load() as PaymentsDO;

            // Hide both containers initially
            const paymentList = this.shadowRoot?.querySelector('.tbw-payment-list') as HTMLElement;
            const missingPayments = this.shadowRoot?.querySelector('.missing-payments') as HTMLElement;

            if (paymentList) paymentList.style.display = 'none';
            if (missingPayments) missingPayments.style.display = 'none';

            if (!this.paymentsData || this.paymentsData.payments.length === 0) {
                this.showMissingPayments();
                return;
            }

            this.showPaymentList();
            this.style.display = 'flex';
        } catch (error) {
            console.error('Error initializing payments:', error);
            this.showMissingPayments();
        } finally {
            this.setupAnalyticsEventListener()
        }
    }

    setupAnalyticsEventListener() {
        const links = this.shadowRoot?.querySelectorAll(".download-link")
        links?.forEach((l) => {
            l.addEventListener('click', () => {
                window.tbwServices.analyticsService.track('invoice_click')
            })
        })
    }

    async load() {
        return await this.profileService.getBillingData();
    }

    showMissingPayments() {
        const missingPayments = this.shadowRoot?.querySelector('.missing-payments') as HTMLElement;
        if (missingPayments) {
            missingPayments.style.display = 'flex';
            this.style.display = 'flex';
        }
        // Inject HTML for the description
        const descElem = this.shadowRoot?.querySelector('.missing-payments-description');
        if (descElem) {
            descElem.innerHTML = i18n.t('payment-list-missing-payments-description');
        }
    }

    showPaymentList() {
        if (!this.shadowRoot) { return; }
        const formatter = new Formatter();

        const locale = i18n.getLanguage() === 'fr' ? 'fr-FR' : 'en-US';

        const paymentList = this.shadowRoot.querySelector('.tbw-payment-list') as HTMLElement;
        if (paymentList) {
            paymentList.style.display = 'flex';
        }

        this.shadowRoot!.querySelector('tbody')!.innerHTML = this.paymentsData.payments.map((payment: Payment) => {
            const createdDate = formatter.dateNumberToDate(locale, payment.created);
            const description = payment.description === 'subscription_create' ? i18n.t('payment-list-subscription-create') : payment.description === 'subscription_update' ? i18n.t('payment-list-subscription-update') : i18n.t('payment-list-subscription-cycle');
            const status = i18n.t(STATUS_TRANSLATIONS[payment.status]);
            return `
            <tr>
                <td>${description}</td>
                <td>${status}</td>
                <td>${formatter.stripeAmount(payment.amount_due, locale)}</td>
                <td>${createdDate ?? ''}</td>
                ${payment.link ? `<td class="download-link-container"><a href="${payment.link}" class="download-link">${downloadIcon}</a></td>` : '<td></td>'}
            </tr>
        `;
        }).join('');
    }
}
